import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Equipment, EquipmentUpdate } from '../models/equipment.model';

@Injectable({ providedIn: 'root' })
export class EquipmentService {
  private http = inject(HttpClient);
  private apiUrl = '/quotes-api/equipment/';

  private equipmentSubject = new BehaviorSubject<Equipment[]>([]);
  public equipment$ = this.equipmentSubject.asObservable();

  private activeEquipmentSubject = new BehaviorSubject<Equipment[]>([]);
  public activeEquipment$ = this.activeEquipmentSubject.asObservable();

  constructor() {
    void this.loadEquipment();
  }

  async loadEquipment(): Promise<void> {
    const rows = await firstValueFrom(this.http.get<unknown[]>(this.apiUrl));
    const parsed = rows.map((r) => Equipment.parse(r));
    this.activeEquipmentSubject.next(parsed.filter((e) => !e.is_archived));
    this.equipmentSubject.next(parsed);
  }

  async createEquipment(data: EquipmentUpdate): Promise<Equipment> {
    const created = await firstValueFrom(
      this.http.post<unknown>(this.apiUrl, data),
    );
    await this.loadEquipment();
    return Equipment.parse(created);
  }

  async putEquipment(id: number, data: EquipmentUpdate): Promise<Equipment> {
    const updated = await firstValueFrom(
      this.http.put<unknown>(`${this.apiUrl}${id}/`, data),
    );
    await this.loadEquipment();
    return Equipment.parse(updated);
  }

  async archiveEquipment(id: number): Promise<void> {
    await firstValueFrom(
      this.http.patch(`${this.apiUrl}${id}/`, { is_archived: true }),
    );
    await this.loadEquipment();
  }

  async unarchiveEquipment(id: number): Promise<void> {
    await firstValueFrom(
      this.http.patch(`${this.apiUrl}${id}/`, { is_archived: false }),
    );
    await this.loadEquipment();
  }
}
